import { ButtonHTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, disabled, children, ...props }, ref) => {
    const getVariantStyles = () => {
      // Read CSS variables at render time so hover colors respect white-label config
      const computedStyle = getComputedStyle(document.documentElement);
      const accentColor = computedStyle.getPropertyValue('--wl-accent').trim() || '#E94B60';
      const accentLightColor = computedStyle.getPropertyValue('--wl-accent-light').trim() || 'rgba(233, 75, 96, 0.04)';
      const secondaryColor = computedStyle.getPropertyValue('--wl-secondary').trim() || '#22212C';

      const baseStyles = {
        primary: {
          backgroundColor: 'var(--wl-primary)',
          color: '#FFF',
          border: 'none',
          boxShadow: '0 2px 4px 0 rgba(233, 75, 96, 0.4)',
        },
        primaryHover: {
          backgroundColor: accentColor,
          transform: 'translateY(-1px)',
        },
        secondary: {
          backgroundColor: '#8592A3',
          color: '#FFF',
          border: 'none',
        },
        secondaryHover: {
          backgroundColor: '#97A2B1',
        },
        outline: {
          backgroundColor: 'transparent',
          color: 'var(--wl-primary)',
          border: '2px solid var(--wl-primary)',
        },
        outlineHover: {
          backgroundColor: accentLightColor,
        },
        ghost: {
          backgroundColor: 'transparent',
          color: 'var(--wl-secondary)',
          border: 'none',
        },
        ghostHover: {
          backgroundColor: `${secondaryColor}0A`,
        },
        danger: {
          backgroundColor: '#E5384F',
          color: '#FFF',
          border: 'none',
        },
        dangerHover: {
          backgroundColor: '#FF5B3F',
        },
      };

      return baseStyles;
    };

    const getSizeStyles = () => {
      switch (size) {
        case 'sm':
          return { padding: '6px 12px', fontSize: '0.875rem', borderRadius: '4px' };
        case 'lg':
          return { padding: '12px 24px', fontSize: '1.125rem', borderRadius: '8px' };
        default:
          return { padding: '7px 20px', fontSize: '1rem', borderRadius: '6px' };
      }
    };

    const styles = getVariantStyles();
    const sizeStyles = getSizeStyles();
    const variantStyle = styles[variant as keyof typeof styles];

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={clsx(
          'inline-flex items-center justify-center font-medium transition-all',
          'focus:outline-none focus:ring-2 focus:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className
        )}
        style={{
          ...variantStyle,
          ...sizeStyles,
          fontWeight: 500,
          letterSpacing: '0.3px',
          lineHeight: '24px',
          transition: 'all 0.2s ease-in-out',
        }}
        onMouseEnter={(e) => {
          if (!disabled && !isLoading) {
            const hoverStyle = styles[`${variant}Hover` as keyof typeof styles];
            Object.assign(e.currentTarget.style, hoverStyle);
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && !isLoading) {
            Object.assign(e.currentTarget.style, variantStyle, sizeStyles, {
              fontWeight: 500,
              letterSpacing: '0.3px',
              lineHeight: '24px',
              transition: 'all 0.2s ease-in-out',
            });
          }
        }}
        {...props}
      >
        {isLoading && (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
